import NumberFlow from '@number-flow/react';
import React, { useEffect, useState } from 'react';

import type { HomeProofStat } from '@/data/shared/homeProofStats';

interface ProofStatCounterProps {
  stat: HomeProofStat;
  /** Intl locale tag the number prints in */
  locale: string;
  className?: string;
}

/**
 * Mounts at zero and rolls up to the figure on the next frame. The first
 * paint is never animated by the counter, so the roll starts from a printed
 * zero rather than from whatever the previous figure was.
 */
function CountingNumber({ stat, locale, className }: ProofStatCounterProps) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setValue(stat.value));

    return () => cancelAnimationFrame(frame);
  }, [stat.value]);

  return (
    <NumberFlow
      value={value}
      format={stat.format}
      locales={locale}
      suffix={stat.suffix}
      className={className}
      aria-label={stat.figure}
    />
  );
}

/**
 * ### ProofStatCounter
 *
 * The figure as a number that counts up to its value. Each entry gets its
 * own counter, remounted when the entry changes, so a new figure is seen
 * being reached from zero rather than the digits of one figure sliding into
 * another. The suffix is part of the figure and is printed by the same
 * element. Motion preferences are respected by the counter itself.
 *
 * @returns The rendered counter element
 */
export default function ProofStatCounter(props: ProofStatCounterProps) {
  return <CountingNumber key={props.stat.id} {...props} />;
}
